/**
 * PreviousAnswersStrip.tsx — Collapsible list of already-answered questions in current CP
 *
 * W21: Primary Answer Surface
 * Default: collapsed
 */

import { useState } from "react";
import type { Question, AnswerRecord } from "@/hooks/useAnswerLane";

interface Props {
  cpNumber: number;
  questions: Question[];
  /** Currently active question key — skip this one in the strip */
  activeKey: string;
}

export default function PreviousAnswersStrip({ cpNumber, questions, activeKey }: Props) {
  const [open, setOpen] = useState(false);

  const answered = questions.filter(
    (q) => q.cp_number === cpNumber && q.is_answered && q.question_key !== activeKey,
  );

  if (answered.length === 0) return null;

  return (
    <div
      style={{
        border: "1px solid #E5E5E7",
        borderRadius: 8,
        overflow: "hidden",
        marginTop: 12,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "9px 12px",
          background: "#FAFAF7",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          borderBottom: open ? "1px solid #E5E5E7" : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              color: "#6B6B70",
            }}
          >
            Previous answers in this Checkpoint
          </span>
          <span
            style={{
              fontSize: 10,
              color: "#9CA3AF",
              fontWeight: 500,
            }}
          >
            ({answered.length})
          </span>
        </div>
        <span style={{ fontSize: 12, color: "#9CA3AF" }}>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            background: "#FFFFFF",
          }}
        >
          {answered.map((q) => {
            const ans = q.current_answer;
            return (
              <li
                key={q.question_key}
                style={{
                  padding: "9px 12px",
                  borderBottom: "1px solid #F4F4F5",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: "#0B0B0F",
                    lineHeight: 1.4,
                  }}
                >
                  {q.question_text}
                </span>
                {ans?.answer_value && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#525258",
                    }}
                  >
                    {ans.answer_value}
                    {ans.is_evidence ? (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 10,
                          color: "#16A34A",
                          fontWeight: 600,
                        }}
                      >
                        Evidence
                      </span>
                    ) : (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 10,
                          color: "#CA8A04",
                          fontWeight: 600,
                        }}
                      >
                        Assumption
                      </span>
                    )}
                  </span>
                )}
                {ans?.answer_notes && (
                  <span
                    style={{
                      fontSize: 11,
                      color: "#6B6B70",
                      fontStyle: "italic",
                      lineHeight: 1.4,
                    }}
                  >
                    {ans.answer_notes}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
